import type {
  ChatParticipant,
  SelectedEmailMetadata,
  WithChatMessageMetadata,
} from '@/lib/chat-message-metadata'

import type { AutomationState } from '@/lib/data/convex/automations-transforms'
import type { ChatStreamStatus } from '@/lib/data/convex/chats'
import type { EmailState } from '@/lib/data/convex/email-state'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const PENDING_PROMPT_KEY = 'orchestrator-pending-prompt'
export const PUBLIC_COMPOSER_DRAFT_KEY = 'orchestrator-public-composer-draft'
export function brandPrefKey(orgId: string): string {
  return `orchestrator-brand-preference:${orgId}`
}

export const STREAMDOWN_ANIMATION = {
  animation: 'blurIn' as const,
  duration: 200,
  easing: 'ease-out',
  sep: 'word' as const,
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PendingPrompt = {
  chatId: string
  orgId: string
  text: string
  modelId?: string
  awaitingAuth?: boolean
  files?: Array<{
    url: string
    mediaType: string
    filename?: string
    previewText?: string
    isHtmlAttachment?: boolean
  }>
  /**
   * IndexedDB key holding inline email-source files (multi-MB `data:` URLs
   * that would blow the localStorage quota); the hand-off re-stages them.
   */
  filesKey?: string
  customBrandId?: string
  /**
   * Email chips staged by a client-first handoff. The chat thread writes
   * these into the first user message's canonical `data-selected-emails`
   * part so selection survives replay and transcript persistence.
   */
  selectedEmails?: Array<SelectedEmailMetadata>
  /**
   * Stable id for the user message built from this prompt. Reused on a
   * replay (reload before the orchestrator persisted the message) so
   * the route's same-id tail replacement makes the re-submit idempotent.
   */
  messageId?: string
  /**
   * Set by the flow-template page's Remix launcher: the public flow the
   * first turn remixes. One-shot — only the first POST of the chat carries
   * it; follow-ups are ordinary automation turns.
   */
  remixSourceFlowSlug?: string
  /** Epoch ms when the prompt was written. Drives the read-side TTL. */
  createdAt?: number
  /**
   * Flipped `true` the moment the thread fires the orchestrator POST for
   * this prompt. A reload that finds `submitted: true` must NOT blindly
   * re-submit — it waits for the live Convex snapshot and only replays
   * when the chat is still empty (the POST never landed).
   */
  submitted?: boolean
}

export type PublicComposerDraftAttachment = {
  id: string
  filename: string
  mediaType: string
  previewText?: string
  isHtmlAttachment?: boolean
}

export type PublicComposerDraft = {
  chatId: string
  text: string
  modelId?: string
  filesKey?: string
  files?: Array<PublicComposerDraftAttachment>
  customBrandId?: string
  /** Written by the signed-out flow Remix box; consumed by the flow page's launcher. */
  remixSourceFlowSlug?: string
}

export type ArtifactNavigationLookup = {
  emailIds: Record<string, string>
  automationIds: Record<string, string>
}

export type ChatArtifactRef =
  | { kind: 'email'; id: string }
  | { kind: 'automation'; id: string }

export type SubAgentTab = {
  id: string
  toolName: string
  label: string
  title: string
  emailId?: string
  automationId?: string
  status: 'pending' | 'streaming' | 'complete' | 'error'
}

type ChatComposerSharedProps = {
  initialOrgId: string
  initialModelId?: string
  mentionEmails?: Array<EmailState>
  mentionAutomations?: Array<AutomationState>
}

/**
 * Props for the full chat experience (`/chat/[chatId]` and canvas chat
 * thread). NEW chat creation flows through the `/home` launcher
 * (`components/chat-launch/launch-composer.tsx` — pending-prompt handoff),
 * not through these props.
 */
export type FullChatComponentProps = ChatComposerSharedProps & {
  chatId: string
  initialMessages?: Array<WithChatMessageMetadata>
  initialEmails?: Array<EmailState>
  initialAutomations?: Array<AutomationState>
  initialStreamStatus?: ChatStreamStatus
  currentUser?: ChatParticipant | null
  participants?: Record<string, ChatParticipant>
  focusedArtifact?: ChatArtifactRef | null
  onOpenArtifact?: (artifact: ChatArtifactRef) => void
  resume?: boolean
}

export type ChatComponentProps = FullChatComponentProps
